import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { SourceBadge, Tag } from './status-badge';
import {
  ANY_DIMENSION,
  GLOBAL_EVENT,
  dimensionLabel,
  formatDimension,
  matchesExpectedPrefix,
  pick,
  parseDimension,
  rowStatus,
  type AccountOption,
  type CoverageRow,
  type Lang,
  type PostingCatalog,
} from './model';

export interface LegRowsProps {
  catalog: PostingCatalog;
  lang: Lang;
  /** Catalog event id, or "*" on the defaults tab. */
  event: string;
  role: string;
  /** Dimension keys this slot can be specialised by. */
  dimensions: string[];
  rows: CoverageRow[];
  accountOptions: AccountOption[];
  canEdit: boolean;
  busyKey: string | null;
  onSave: (dimension: string, accountId: string) => void;
  onRemove: (ruleId: string) => void;
}

const NONE = '__none__';

/**
 * The routing table of ONE slot (event × role): a row per dimension showing
 * the account the engine resolves right now, where it came from, and an
 * account picker to override it. Extra dimension combinations are added with
 * the "+ özel tanım" row.
 */
export function LegRows({ catalog, lang, event, role, dimensions, rows, accountOptions, canEdit, busyKey, onSave, onRemove }: LegRowsProps) {
  const { t } = useTranslation('postingRules');
  const roleDef = catalog.roles.find((r) => r.role === role);
  const expectedPrefixes = roleDef?.expectedPrefixes ?? [];
  const scope = roleDef?.scopeDimension;
  const scopedValues = catalog.dimensions.find((d) => d.key === scope)?.values ?? [];
  const groupOf = (row: CoverageRow) => scope ? parseDimension(row.dimension)[scope] ?? '' : '';
  const groupOrder = (row: CoverageRow) => {
    const index = scopedValues.findIndex((v) => v.value === groupOf(row));
    return index < 0 ? scopedValues.length : index;
  };
  const displayRows = scope ? [...rows].sort((a, b) => groupOrder(a) - groupOrder(b)) : rows;
  const [adding, setAdding] = useState(false);
  const [newParts, setNewParts] = useState<Record<string, string>>({});
  const [newAccount, setNewAccount] = useState(NONE);

  const newDimension = formatDimension(newParts, dimensions);
  const duplicate = rows.some((r) => r.dimension === newDimension);
  const options = useMemo(() => [{ value: NONE, label: t('leg.keepDefault'), keywords: [], code: '' }, ...accountOptions], [accountOptions, t]);

  const resetAdd = () => {
    setAdding(false);
    setNewParts({});
    setNewAccount(NONE);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">{t('leg.dimension')}</th>
            <th className="px-3 py-2 font-medium">{t('leg.resolvedAccount')}</th>
            <th className="px-3 py-2 font-medium">{t('leg.source')}</th>
            <th className="px-3 py-2 font-medium">{t('leg.override')}</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, index) => {
            const status = rowStatus(row);
            const key = `${event}|${role}|${row.dimension}`;
            const busy = busyKey === key;
            const eventRule = row.rule && row.rule.event === event && event !== GLOBAL_EVENT ? row.rule : null;
            const globalRule = row.rule && row.rule.event === GLOBAL_EVENT ? row.rule : null;
            const prefixOk = matchesExpectedPrefix(row.resolved?.accountCode, expectedPrefixes);
            const group = groupOf(row);
            const groupLabel = scopedValues.find((v) => v.value === group)?.label;
            const displayDimension = scope && group
              ? formatDimension(Object.fromEntries(Object.entries(parseDimension(row.dimension)).filter(([key]) => key !== scope)), roleDef?.dimensions ?? dimensions)
              : row.dimension;
            return (
              <Fragment key={row.dimension}>
              {scope && (index === 0 || group !== groupOf(displayRows[index - 1]!)) && (
                <tr className="border-b border-border bg-muted/40">
                  <th colSpan={5} scope="colgroup" className="px-3 py-3 text-left font-semibold">
                    {groupLabel ? pick(groupLabel, lang) : t('leg.unclassifiedRevenue')}
                  </th>
                </tr>
              )}
              <tr className="border-b border-border last:border-b-0 align-top">
                <td className="px-3 py-2 whitespace-nowrap">
                  {displayDimension === ANY_DIMENSION ? (
                    <span className="font-medium">{t('leg.anyDimension')}</span>
                  ) : (
                    dimensionLabel(displayDimension, catalog, lang, t('leg.anyDimension'))
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.resolved ? (
                    <div>
                      <span className="font-mono text-xs">{row.resolved.accountCode}</span>{' '}
                      <span className="text-muted-foreground">{row.resolved.accountName}</span>
                      {!prefixOk && (
                        <div className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-300">
                          {t('leg.prefixWarning', { prefixes: expectedPrefixes.join(', ') })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-red-700 dark:text-red-300">
                      {row.defaultCode ? t('leg.missingDefault', { code: row.defaultCode }) : t('leg.noDefault')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex flex-wrap items-center gap-1">
                    <SourceBadge status={status} />
                    {row.resolved?.source === 'party' && <Tag>{t('status.party')}</Tag>}
                    {globalRule && event !== GLOBAL_EVENT && !eventRule && <Tag>{t('leg.fromGlobal')}</Tag>}
                    {row.rule && !row.rule.isActive && <Tag className="text-red-700">{t('status.inactiveAccount')}</Tag>}
                    {row.parentAccount && <Tag className="text-amber-700">{t('status.parentAccount')}</Tag>}
                    {row.resolved?.source === 'default' && row.resolved.dimension && row.resolved.dimension !== row.dimension && (
                      <Tag>{t('leg.viaDimension', { dimension: dimensionLabel(row.resolved.dimension, catalog, lang, t('leg.anyDimension')) })}</Tag>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 min-w-[260px]">
                  {canEdit ? (
                    <div className="flex items-center gap-2">
                      <Combobox
                        options={options}
                        value={eventRule?.accountId ?? (event === GLOBAL_EVENT ? globalRule?.accountId : undefined) ?? NONE}
                        onValueChange={(v) => {
                          if (v === NONE) {
                            const current = event === GLOBAL_EVENT ? globalRule : eventRule;
                            if (current) onRemove(current.id);
                          } else {
                            onSave(row.dimension, v);
                          }
                        }}
                        placeholder={t('leg.pickAccount')}
                        searchPlaceholder={t('leg.searchAccount')}
                        emptyText={t('leg.noAccounts')}
                        disabled={busy}
                        className="flex-1"
                      />
                      {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t('leg.readOnly')}</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {canEdit && (event === GLOBAL_EVENT ? globalRule : eventRule) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={busy}
                      onClick={() => onRemove((event === GLOBAL_EVENT ? globalRule : eventRule)!.id)}
                      title={event === GLOBAL_EVENT ? t('leg.resetToDefault') : t('leg.resetToGlobal')}
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      {event === GLOBAL_EVENT ? t('leg.resetToDefault') : t('leg.resetToGlobal')}
                    </Button>
                  )}
                </td>
              </tr>
              </Fragment>
            );
          })}

          {canEdit && dimensions.length > 0 && (
            <tr className="bg-muted/30">
              {adding ? (
                <>
                  <td className="px-3 py-2" colSpan={2}>
                    <div className="flex flex-wrap gap-2">
                      {dimensions.map((key) => {
                        const def = catalog.dimensions.find((d) => d.key === key);
                        return (
                          <Select
                            key={key}
                            value={newParts[key] ?? ''}
                            onValueChange={(v) => setNewParts((p) => ({ ...p, [key]: v === '__any__' ? '' : v }))}
                          >
                            <SelectTrigger className="h-8 w-[180px] text-xs">
                              <SelectValue placeholder={def ? pick(def.label, lang) : key} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__any__">{t('leg.anyValue', { dimension: def ? pick(def.label, lang) : key })}</SelectItem>
                              {(def?.values ?? []).map((v) => (
                                <SelectItem key={v.value} value={v.value}>
                                  {pick(v.label, lang)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      })}
                    </div>
                    {duplicate && <p className="mt-1 text-[11px] text-amber-700">{t('leg.duplicateDimension')}</p>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{newDimension === ANY_DIMENSION ? t('leg.pickAtLeastOne') : newDimension}</td>
                  <td className="px-3 py-2 min-w-[260px]">
                    <Combobox
                      options={options}
                      value={newAccount}
                      onValueChange={setNewAccount}
                      placeholder={t('leg.pickAccount')}
                      searchPlaceholder={t('leg.searchAccount')}
                      emptyText={t('leg.noAccounts')}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={newDimension === ANY_DIMENSION || duplicate || newAccount === NONE}
                        onClick={() => {
                          onSave(newDimension, newAccount);
                          resetAdd();
                        }}
                      >
                        {t('leg.save')}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={resetAdd} title={t('leg.cancel')}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </>
              ) : (
                <td className="px-3 py-1.5" colSpan={5}>
                  <button
                    type="button"
                    className={cn('inline-flex items-center gap-1 text-xs text-primary hover:underline')}
                    onClick={() => setAdding(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('leg.addSpecific')}
                  </button>
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
